import { useState, useEffect, useCallback } from "react";
import useTags from "@/hooks/useTags";
import type Tag from "@/models/tag/tag";
import { TagType } from "@/models/tag/tag";
import {
  showSuccessToast,
  showErrorToast,
} from "@/components/custom-toasts/toasts";

interface UseTagsManagementReturn {
  tags: Tag[];
  loading: boolean;
  handleCreateTag: (name: string, tagType: TagType) => Promise<boolean>;
  handleUpdateTag: (
    tagId: string,
    name: string,
    tagType: TagType
  ) => Promise<boolean>;
  handleDeleteTag: (tagId: string) => Promise<boolean>;
  searchTags: (searchTerm: string) => Tag[];
  departmentTags: Tag[];
  regularTags: Tag[];
}

const useTagsManagement = (): UseTagsManagementReturn => {
  const { useQueryPagination, create, update, remove } = useTags();
  const [tags, setTags] = useState<Tag[]>([]);

  // Fetch all tags
  const fetchedTags = useQueryPagination({
    pageNumber: 1,
    pageSize: 1000000,
    sortAscending: true,
  });

  useEffect(() => {
    if (fetchedTags.isFetched && fetchedTags.data && fetchedTags.data.data) {
      setTags(fetchedTags.data.data);
    }
  }, [fetchedTags.data, fetchedTags.isFetched]);

  const handleCreateTag = async (
    name: string,
    tagType: TagType
  ): Promise<boolean> => {
    try {
      await create.mutateAsync({ name, tagType });
      showSuccessToast("Tagget er blevet oprettet");
      return true;
    } catch (error) {
      showErrorToast(
        `Der skete en fejl ved oprettelse af tagget: ${error instanceof Error ? error.message : String(error)}`
      );
      return false;
    }
  };

  const handleUpdateTag = async (
    tagId: string,
    name: string,
    tagType: TagType
  ): Promise<boolean> => {
    try {
      await update.mutateAsync({ id: tagId, name, tagType });
      showSuccessToast("Tagget er blevet opdateret");
      return true;
    } catch (error) {
      showErrorToast(
        `Der skete en fejl ved opdatering af tagget: ${error instanceof Error ? error.message : String(error)}`
      );
      return false;
    }
  };

  const handleDeleteTag = async (tagId: string): Promise<boolean> => {
    try {
      await remove.mutateAsync(tagId);
      showSuccessToast("Tagget er blevet slettet");
      return true;
    } catch (error) {
      showErrorToast(
        `Der skete en fejl ved sletning af tagget: ${error instanceof Error ? error.message : String(error)}`
      );
      return false;
    }
  };

  const searchTags = useCallback(
    (searchTerm: string): Tag[] => {
      if (!searchTerm.trim()) return tags;
      return tags.filter((tag) =>
        tag.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    },
    [tags]
  );

  const departmentTags = tags.filter(
    (tag) => tag.tagType === TagType.Department
  );
  const regularTags = tags.filter((tag) => tag.tagType === TagType.Tag);

  return {
    tags,
    loading: fetchedTags.isLoading,
    handleCreateTag,
    handleUpdateTag,
    handleDeleteTag,
    searchTags,
    departmentTags,
    regularTags,
  };
};

export default useTagsManagement;
